import React, { useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { AdminDashboardHeader } from '../AdminDashboard/AdminDashboardHeader';
import { RichTextEditor } from '../../components/RichTextEditor';
import { MediaUploadModal } from '../../components/MediaUploadModal';
import { useHowToEditor } from './useHowToEditor';
import { generateSlugFromTitle } from './utils';

export const HowToEditorPage: React.FC = () => {
  const {
    authLoading,
    isAuthenticated,
    isAdmin,
    logout,
    isNew,
    form,
    categories,
    isLoading,
    isSaving,
    error,
    updateField,
    handleContentImageUpload,
    addGalleryImage,
    removeGalleryImage,
    saveDraft,
    savePublish,
    cancel,
  } = useHowToEditor();

  const [showGalleryModal, setShowGalleryModal] = useState(false);

  if (authLoading || isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 spinner" />
          <p className="text-gray-600 font-medium">
            {authLoading ? 'Authenticating...' : 'Loading editor...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) return null;

  return (
    <div className="page-container">
      <AdminDashboardHeader onLogout={logout} />
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-xl font-bold text-gray-900">
              {isNew ? 'New How To guide' : 'Edit How To guide'}
            </h1>
            <div className="flex items-center gap-2">
              <button type="button" onClick={cancel} className="btn-ghost" disabled={isSaving}>
                Cancel
              </button>
              <button
                type="button"
                onClick={saveDraft}
                className="btn-secondary"
                disabled={isSaving}
              >
                Save draft
              </button>
              <button
                type="button"
                onClick={savePublish}
                className="btn-primary"
                disabled={isSaving}
              >
                Save & publish
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={e => updateField('title', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="How to publish a page"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL slug</label>
            <input
              type="text"
              value={form.slug}
              onChange={e => updateField('slug', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={generateSlugFromTitle(form.title) || 'url-slug'}
            />
            <p className="mt-1 text-xs text-gray-500">
              Auto-generated from the title until you edit it. Used in /help/:slug
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={form.categoryId}
                onChange={e => updateField('categoryId', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                {categories.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={e => updateField('status', e.target.value as 'draft' | 'published')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort order</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={e => updateField('sortOrder', Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
            <RichTextEditor
              content={form.contentHtml}
              onChange={html => updateField('contentHtml', html)}
              placeholder="Write the guide content..."
              minHeight="360px"
              onImageUpload={handleContentImageUpload}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Video URL <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <input
              type="url"
              value={form.videoUrl}
              onChange={e => updateField('videoUrl', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="https://www.youtube.com/watch?v=... or Vimeo URL"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Image gallery{' '}
                <span className="font-normal text-gray-500">
                  ({form.galleryJson.length}/10, optional)
                </span>
              </label>
              <button
                type="button"
                onClick={() => setShowGalleryModal(true)}
                disabled={form.galleryJson.length >= 10}
                className="btn-secondary text-sm inline-flex items-center gap-1 disabled:opacity-50"
              >
                <ImagePlus size={16} />
                Add image
              </button>
            </div>
            {form.galleryJson.length === 0 ? (
              <p className="text-sm text-gray-500">No gallery images yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {form.galleryJson.map((item, index) => (
                  <div
                    key={`${item.url}-${index}`}
                    className="relative group rounded-lg overflow-hidden border border-gray-200"
                  >
                    <img
                      src={item.cloudfrontUrl || item.url}
                      alt={item.alt || ''}
                      className="w-full h-28 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(index)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove image"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <MediaUploadModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        mediaType="image"
        componentType="howto"
        assetType="image"
        onMediaUploaded={(url, cloudfrontUrl) => {
          addGalleryImage(url, cloudfrontUrl);
          setShowGalleryModal(false);
        }}
      />
    </div>
  );
};
